
import { Component } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatToolbarModule } from '@angular/material/toolbar';
import * as mime from 'mime';
import { ObservationsService } from 'src/app/services/observations.service';
import { EVSidebarService } from '../ev-sidebar/ev-sidebar.service';

@Component({
  selector: 'app-ev-export',
  templateUrl: './ev-export.component.html',
  styleUrls: ['./ev-export.component.scss'],
  standalone: true,
  imports: [MatToolbarModule, MatIconModule, MatRadioModule, ReactiveFormsModule, MatButtonModule],
})
export class EvExportComponent {
  public form: UntypedFormGroup;
  public resetDisabled = true;
  public exportDisabled = true;
  public observationOptions: Array<Option>;
  public formatOptions: Array<Option>;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly sidebarService: EVSidebarService,
    private readonly observationsService: ObservationsService,
  ) {
    // generate options from enums
    this.observationOptions = Object.keys(ObservationOptionsEnum).map(
      (item) => new Option(item, ObservationOptionsEnum[item]),
    );
    this.formatOptions = Object.keys(FormatOptionsEnum).map((item) => new Option(item, FormatOptionsEnum[item]));
    // disable all but json
    this.formatOptions.forEach((option) => (option.disabled = option.value !== FormatOptionsEnum.JSON));

    this.initialiseSearchForm();

    const filterOption = this.observationOptions.find((item) => item.value === ObservationOptionsEnum.FILTERED);
    observationsService.watchFilterParameters().subscribe((filterParameters: object) => {
      const allEmpty =
        null == filterParameters || Object.values(filterParameters).every((value) => !Boolean(value).valueOf());
      filterOption.disabled = allEmpty;
      filterOption.name = `${ObservationOptionsEnum.FILTERED}${allEmpty ? ' (No filter set)' : ''}`;

      // if filter is disabled and selected deselect it
      const control = this.form.get('download_options');
      if (allEmpty && control.value === ObservationOptionsEnum.FILTERED) {
        control.setValue(null);
      }
    });
  }

  public closeSidePanel() {
    this.sidebarService.clearSidebarMode();
  }

  public initialiseSearchForm(): void {
    this.form = this.fb.group({
      download_options: [null, [Validators.required]],
      // geo_limits: [null, []],
      download_formats: [null, [Validators.required]],
    });
    this.form.statusChanges.subscribe(() => {
      this.exportDisabled = !this.form.valid;
    });
    this.form.valueChanges.subscribe(() => {
      this.resetDisabled = !this.form.dirty;
    });
  }

  public reset(): void {
    this.form.reset();
  }
  public submit(): void {
    this.form.disable();
    this.observationsService
      .searchForExport(this.form.get('download_options').value === ObservationOptionsEnum.FILTERED)
      .then((data: Array<object>) => {
        this.setDownloadData(data);
      })
      .finally(() => {
        this.form.enable();
      });
  }

  private setDownloadData(data: Array<object>): void {
    let dataString: string;
    let fileExtension: string; // extension - used for finding mime type

    switch (this.form.get('download_formats').value) {
      case FormatOptionsEnum.JSON:
        dataString = JSON.stringify(data);
        fileExtension = 'json';
        break;
    }

    const anchor = document.createElement('a');
    document.body.appendChild(anchor);
    const windowUrl = window.URL;
    const mimeType = mime.getType(fileExtension);
    const blob = new Blob([dataString], { type: mimeType });
    const thisUrl = windowUrl.createObjectURL(blob);
    anchor.href = thisUrl;
    // console.log('mime', fileExtension, mimeType);
    anchor.download = `download.${fileExtension}`;
    anchor.click();
    windowUrl.revokeObjectURL(thisUrl);
    anchor.parentElement.removeChild(anchor);
  }
}

class Option {
  constructor(public name, public readonly value, public disabled = false) {}
}

enum ObservationOptionsEnum {
  FILTERED = 'Filtered observations',
  ALL = 'All observations',
}
enum FormatOptionsEnum {
  JSON = 'JSON',
  GEOJSON = 'GeoJSON',
  CSV = 'CSV',
}
